import React, { useEffect, useState, useMemo } from 'react';
import { customerService, planService, branchService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import Table from '../components/Table';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Input from '../components/Input';
import Select from '../components/Select';
import { FiPlus, FiEdit, FiTrash2, FiPause, FiPlay, FiFileText, FiUserPlus, FiChevronDown, FiChevronUp, FiGitBranch, FiKey, FiUser, FiCalendar, FiClock } from 'react-icons/fi';

// Number of days for each billing cycle — kept in sync with backend
// customerController so client-side previews match server writes.
const CYCLE_DAYS = { monthly: 30, quarterly: 90, yearly: 365 };
const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};
// yyyy-mm-dd formatter for the native <input type="date"> value.
const toDateInputValue = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const SectionToggle = ({ label, checked, onChange, description }) => (
  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-dashed border-gray-300 hover:border-purple-400 hover:bg-purple-50/40 transition-all select-none">
    <div className={`w-9 h-5 rounded-full transition-colors ${checked ? 'bg-purple-600' : 'bg-gray-300'} relative flex-shrink-0`}>
      <div className={`w-3.5 h-3.5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
    </div>
    <div>
      <p className="text-sm font-medium text-gray-800">{label}</p>
      {description && <p className="text-xs text-gray-400">{description}</p>}
    </div>
  </label>
);

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const { user } = useAuth();
  const currencySymbol = user?.resellerId?.currency?.symbol || 'RM';
  const [createBranchManager, setCreateBranchManager] = useState(true);
  const [createBranch, setCreateBranch] = useState(true);
  const [sameAsAdmin, setSameAsAdmin] = useState(true);
  const [formData, setFormData] = useState({
    businessName: '', businessEmail: '', businessPhone: '',
    planId: '', adminName: '', adminEmail: '', adminPassword: '',
    branchManager: { name: '', email: '', password: '' },
    branch: { name: '', email: '', phone: '', address: { street: '', city: '', state: '', country: '' } },
    owner: { name: '', email: '', phone: '' },
    businessAddress: { street: '', city: '', state: '', country: '', zipCode: '' },
    billingCycle: 'monthly',
    subscriptionStartsAt: toDateInputValue(new Date()),
  });

  const handleSameAsAdminToggle = (checked) => {
    setSameAsAdmin(checked);
    setFormData((prev) => ({
      ...prev,
      branchManager: checked
        ? { name: prev.adminName, email: prev.adminEmail, password: prev.adminPassword }
        : { name: '', email: '', password: '' },
    }));
  };

  const handleAdminFieldChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      if (sameAsAdmin) {
        updated.branchManager = {
          name: field === 'adminName' ? value : prev.adminName,
          email: field === 'adminEmail' ? value : prev.adminEmail,
          password: field === 'adminPassword' ? value : prev.adminPassword,
        };
      }
      return updated;
    });
  };

  const handlePlanChange = (planId) => {
    setFormData((prev) => ({ ...prev, planId }));
  };

  useEffect(() => { fetchCustomers(); fetchPlans(); }, []);

  const fetchCustomers = async () => {
    try {
      const res = await customerService.getAll();
      setCustomers(res.data.data);
    } catch { toast.error('Failed to fetch merchants'); }
    finally { setLoading(false); }
  };

  const fetchPlans = async () => {
    try {
      const res = await planService.getAll();
      setPlans(res.data.data);
    } catch { toast.error('Failed to fetch plans'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await customerService.update(currentCustomer._id, formData);
        toast.success('Merchant updated');
      } else {
        const payload = { ...formData, createBranchManager, createBranch };
        if (!createBranchManager) delete payload.branchManager;
        if (!createBranch) delete payload.branch;
        await customerService.create(payload);
        toast.success('Merchant created — welcome email sent!');
      }
      setShowModal(false);
      resetForm();
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (c) => {
    setCurrentCustomer(c);
    setFormData({
      businessName: c.businessName, businessEmail: c.businessEmail,
      businessPhone: c.businessPhone || '',
      planId: c.planId?._id || c.planId || '',
      owner: c.owner || { name: '', email: '', phone: '' },
      businessAddress: c.businessAddress || { street: '', city: '', state: '', country: '', zipCode: '' },
      billingCycle: c.billingCycle || 'monthly',
      subscriptionStartsAt: toDateInputValue(c.subscriptionStartsAt || c.createdAt),
    });
    setEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this merchant?')) return;
    try {
      await customerService.delete(id);
      toast.success('Merchant deleted');
      fetchCustomers();
    } catch { toast.error('Failed to delete merchant'); }
  };

  const handleSeedTemplates = async (c) => {
    if (!window.confirm(`Seed invoice templates for "${c.businessName}"? This will create/update default templates for all branches.`)) return;
    try {
      const res = await customerService.seedTemplates(c._id);
      toast.success(res.data.message || 'Templates seeded successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to seed templates');
    }
  };

  const handleSuspend = async (c) => {
    try {
      await customerService.suspend(c._id, 'Manual suspension');
      toast.success(c.isSuspended ? 'Merchant activated' : 'Merchant suspended');
      fetchCustomers();
    } catch { toast.error('Failed to update status'); }
  };

  const resetForm = () => {
    setFormData({
      businessName: '', businessEmail: '', businessPhone: '', planId: '',
      adminName: '', adminEmail: '', adminPassword: '',
      branchManager: { name: '', email: '', password: '' },
      branch: { name: '', email: '', phone: '', address: { street: '', city: '', state: '', country: '' } },
      owner: { name: '', email: '', phone: '' },
      businessAddress: { street: '', city: '', state: '', country: '', zipCode: '' },
      billingCycle: 'monthly',
      subscriptionStartsAt: toDateInputValue(new Date()),
    });
    setEditMode(false);
    setCurrentCustomer(null);
    setCreateBranchManager(true);
    setCreateBranch(true);
    setSameAsAdmin(true);
    setShowCreateUser(false);
    setCreateUserForm({ role: 'branch_manager', name: '', email: '', password: '' });
    setCreateUserWithBranch(false);
    setCreateUserBranch({ name: '', email: '', phone: '' });
    setCreateUserLoading(false);
  };

  // ── View branches for a merchant ───────────────────────────────────────────
  const [showBranchesModal, setShowBranchesModal] = useState(false);
  const [branchesCustomer, setBranchesCustomer] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordBranch, setPasswordBranch] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleViewBranches = async (customer) => {
    setBranchesCustomer(customer);
    setShowBranchesModal(true);
    setBranchesLoading(true);
    try {
      const res = await branchService.getByCustomer(customer._id);
      setBranches(res.data.data);
    } catch {
      toast.error('Failed to fetch branches');
    } finally {
      setBranchesLoading(false);
    }
  };

  const handleResetPassword = (branch) => {
    setPasswordBranch(branch);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      await branchService.resetManagerPassword(passwordBranch._id, newPassword);
      toast.success('Manager password updated');
      setShowPasswordModal(false);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  // ── Create user for existing merchant ──────────────────────────────────────
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ role: 'branch_manager', name: '', email: '', password: '' });
  const [createUserWithBranch, setCreateUserWithBranch] = useState(false);
  const [createUserBranch, setCreateUserBranch] = useState({ name: '', email: '', phone: '' });

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateUserLoading(true);
    try {
      const payload = {
        ...createUserForm,
        createBranch: createUserWithBranch,
        ...(createUserWithBranch && { branch: createUserBranch }),
      };
      const res = await customerService.createUser(currentCustomer._id, payload);
      toast.success(res.data.message || 'User created successfully');
      setCreateUserForm({ role: 'branch_manager', name: '', email: '', password: '' });
      setCreateUserWithBranch(false);
      setCreateUserBranch({ name: '', email: '', phone: '' });
      setShowCreateUser(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create user');
    } finally {
      setCreateUserLoading(false);
    }
  };

  const columns = useMemo(() => [
    { header: 'Business', accessor: 'businessName' },
    { header: 'Email', accessor: 'businessEmail' },
    { header: 'Phone', accessor: 'businessPhone' },
    { header: 'Plan', render: (row) => row.planId?.planName || <span className="text-gray-400">—</span> },
    {
      header: 'Joined',
      render: (row) => (
        <span className="inline-flex items-center gap-1 text-xs text-gray-600">
          <FiCalendar size={11} className="text-gray-400" />
          {fmtDate(row.subscriptionStartsAt || row.createdAt)}
        </span>
      ),
    },
    {
      header: 'Expires',
      render: (row) => {
        // Prefer the persisted subscriptionEndsAt; if missing, derive
        // from subscriptionStartsAt (or createdAt) + billing cycle so
        // the reseller sees a value even for legacy records.
        let end = row.subscriptionEndsAt;
        if (!end) {
          const start = row.subscriptionStartsAt || row.createdAt;
          if (start) end = addDays(start, CYCLE_DAYS[row.billingCycle] || 30);
        }
        if (!end) return <span className="text-gray-400">—</span>;
        const days = Math.ceil((new Date(end).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        const color =
          days < 0 ? 'text-red-600'
          : days <= 14 ? 'text-amber-600'
          : 'text-gray-600';
        return (
          <div className={`inline-flex flex-col items-start gap-0.5 text-xs ${color}`}>
            <span className="inline-flex items-center gap-1">
              <FiClock size={11} /> {fmtDate(end)}
            </span>
            <span className="text-[10px] opacity-80">
              {days < 0 ? `expired ${Math.abs(days)}d ago` : `in ${days}d`}
            </span>
          </div>
        );
      },
    },
    { header: 'Branches', render: (row) => row.usage?.branches ?? 0 },
    {
      header: 'Features',
      render: (row) => {
        const count = row.currentPlan?.enabledFeatures?.length || 0;
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            count > 0 ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {count} module{count !== 1 ? 's' : ''}
          </span>
        );
      },
    },
    {
      header: 'Status',
      render: (row) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.isActive && !row.isSuspended ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {row.isActive && !row.isSuspended ? 'Active' : 'Suspended'}
        </span>
      ),
    },
    {
      header: 'Actions',
      render: (row) => (
        <div className="flex items-center gap-1">
          <button onClick={() => handleViewBranches(row)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="View Branches"><FiGitBranch size={15} /></button>
          <button onClick={() => handleEdit(row)} className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors" title="Edit"><FiEdit size={15} /></button>
          <button onClick={() => handleSeedTemplates(row)} className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors" title="Seed Invoice Templates"><FiFileText size={15} /></button>
          <button onClick={() => handleSuspend(row)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title={row.isSuspended ? 'Activate' : 'Suspend'}>
            {row.isSuspended ? <FiPlay size={15} /> : <FiPause size={15} />}
          </button>
          <button onClick={() => handleDelete(row._id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete"><FiTrash2 size={15} /></button>
        </div>
      ),
    },
  ], []);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Merchants</h1>
          <p className="text-sm text-gray-500 mt-0.5">{customers.length} total merchant{customers.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }}>
          <FiPlus size={15} /> Add Merchant
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="spinner" /></div>
      ) : (
        <Table columns={columns} data={customers} />
      )}

      <Modal isOpen={showModal} onClose={() => { setShowModal(false); resetForm(); }} title={editMode ? 'Edit Merchant' : 'Add Merchant'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Row 1: Business core */}
          <div className="grid grid-cols-3 gap-2">
            <Input label="Business Name" name="businessName" value={formData.businessName} onChange={(e) => setFormData({ ...formData, businessName: e.target.value })} required />
            <Input label="Business Email" type="email" name="businessEmail" value={formData.businessEmail} onChange={(e) => setFormData({ ...formData, businessEmail: e.target.value })} required disabled={editMode} />
            <Input label="Phone" name="businessPhone" value={formData.businessPhone} onChange={(e) => setFormData({ ...formData, businessPhone: e.target.value })} />
          </div>

          {/* Row 2: Plan + Billing + Owner */}
          <div className="grid grid-cols-3 gap-2">
            <Select label="Plan" name="planId" value={formData.planId} onChange={(e) => handlePlanChange(e.target.value)} options={plans.map((p) => ({ value: p._id, label: `${p.planName} — ${currencySymbol}${p.pricing.monthly}/mo` }))} required />
            <Select label="Billing" name="billingCycle" value={formData.billingCycle} onChange={(e) => setFormData({ ...formData, billingCycle: e.target.value })} options={[{ value: 'monthly', label: 'Monthly' }, { value: 'quarterly', label: 'Quarterly' }, { value: 'yearly', label: 'Yearly' }]} required />
            <Input label="Owner Name" name="ownerName" value={formData.owner.name} onChange={(e) => setFormData({ ...formData, owner: { ...formData.owner, name: e.target.value } })} />
          </div>

          {/* Subscription window — join date is editable; expiry is
              previewed live from join date + billing cycle so the
              reseller sees exactly what will be saved on the server. */}
          <div className="grid grid-cols-2 gap-2 items-end">
            <Input
              label="Join Date"
              type="date"
              value={formData.subscriptionStartsAt}
              onChange={(e) => setFormData({ ...formData, subscriptionStartsAt: e.target.value })}
              required
            />
            <div className="pb-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Expires On (auto)</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                {(() => {
                  if (!formData.subscriptionStartsAt) return '—';
                  const start = new Date(formData.subscriptionStartsAt);
                  if (isNaN(start.getTime())) return '—';
                  const end = addDays(start, CYCLE_DAYS[formData.billingCycle] || 30);
                  return `${fmtDate(end)} · +${CYCLE_DAYS[formData.billingCycle] || 30}d`;
                })()}
              </div>
            </div>
          </div>

          {/* Row 3: Address compact */}
          <div className="grid grid-cols-4 gap-2">
            <Input label="Street" name="street" value={formData.businessAddress.street} onChange={(e) => setFormData({ ...formData, businessAddress: { ...formData.businessAddress, street: e.target.value } })} />
            <Input label="City" name="city" value={formData.businessAddress.city} onChange={(e) => setFormData({ ...formData, businessAddress: { ...formData.businessAddress, city: e.target.value } })} />
            <Input label="State" name="state" value={formData.businessAddress.state} onChange={(e) => setFormData({ ...formData, businessAddress: { ...formData.businessAddress, state: e.target.value } })} />
            <Input label="Country" name="country" value={formData.businessAddress.country} onChange={(e) => setFormData({ ...formData, businessAddress: { ...formData.businessAddress, country: e.target.value } })} />
          </div>

          {!editMode && (
            <>
              {/* Admin Account */}
              <div className="border-t border-gray-100 pt-3">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Admin Account</p>
                <div className="grid grid-cols-3 gap-2">
                  <Input label="Full Name" name="adminName" value={formData.adminName} onChange={(e) => handleAdminFieldChange('adminName', e.target.value)} required />
                  <Input label="Email" type="email" name="adminEmail" value={formData.adminEmail} onChange={(e) => handleAdminFieldChange('adminEmail', e.target.value)} required />
                  <Input label="Password" type="password" name="adminPassword" value={formData.adminPassword} onChange={(e) => handleAdminFieldChange('adminPassword', e.target.value)} required />
                </div>
              </div>

              {/* Branch Manager + Branch — one section */}
              <div className="border-t border-gray-100 pt-3 space-y-2">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer select-none text-xs">
                    <input type="checkbox" checked={createBranchManager} onChange={(e) => { setCreateBranchManager(e.target.checked); if (!e.target.checked) { setSameAsAdmin(false); setFormData((p) => ({ ...p, branchManager: { name: '', email: '', password: '' } })); } }} className="w-3.5 h-3.5 accent-purple-600 rounded" />
                    <span className="font-medium text-gray-700">Create Branch Manager</span>
                  </label>
                  {createBranchManager && (
                    <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs text-gray-500">
                      <input type="checkbox" checked={sameAsAdmin} onChange={(e) => handleSameAsAdminToggle(e.target.checked)} className="w-3 h-3 accent-purple-600 rounded" />
                      Same as admin
                    </label>
                  )}
                  <label className="flex items-center gap-2 cursor-pointer select-none text-xs ml-auto">
                    <input type="checkbox" checked={createBranch} onChange={(e) => { setCreateBranch(e.target.checked); if (e.target.checked) { setFormData((p) => ({ ...p, branch: { ...p.branch, email: p.businessEmail } })); } else { setFormData((p) => ({ ...p, branch: { name: '', email: '', phone: '', address: { street: '', city: '', state: '', country: '' } } })); } }} className="w-3.5 h-3.5 accent-purple-600 rounded" />
                    <span className="font-medium text-gray-700">Create Branch</span>
                  </label>
                </div>

                {createBranchManager && (
                  <div className="grid grid-cols-3 gap-2">
                    <Input label="Manager Name" name="bmName" value={formData.branchManager.name} onChange={(e) => setFormData((p) => ({ ...p, branchManager: { ...p.branchManager, name: e.target.value } }))} disabled={sameAsAdmin} required />
                    <Input label="Manager Email" type="email" name="bmEmail" value={formData.branchManager.email} onChange={(e) => setFormData((p) => ({ ...p, branchManager: { ...p.branchManager, email: e.target.value } }))} disabled={sameAsAdmin} required />
                    <Input label="Manager Password" type="password" name="bmPassword" value={formData.branchManager.password} onChange={(e) => setFormData((p) => ({ ...p, branchManager: { ...p.branchManager, password: e.target.value } }))} disabled={sameAsAdmin} required />
                  </div>
                )}

                {createBranch && (
                  <div className="grid grid-cols-3 gap-2">
                    <Input label="Branch Name" name="branchName" value={formData.branch.name} onChange={(e) => setFormData((p) => ({ ...p, branch: { ...p.branch, name: e.target.value } }))} required />
                    <Input label="Branch Email" type="email" name="branchEmail" value={formData.branch.email} onChange={(e) => setFormData((p) => ({ ...p, branch: { ...p.branch, email: e.target.value } }))} />
                    <Input label="Branch Phone" name="branchPhone" value={formData.branch.phone} onChange={(e) => setFormData((p) => ({ ...p, branch: { ...p.branch, phone: e.target.value } }))} />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Create Admin / Branch Manager for existing merchant */}
          {editMode && (
            <div className="border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => setShowCreateUser(v => !v)}
                className="w-full flex items-center justify-between text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 px-3 py-2 rounded-lg transition-colors"
              >
                <span className="flex items-center gap-1.5"><FiUserPlus size={13} /> Create Admin / Branch Manager</span>
                {showCreateUser ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}
              </button>

              {showCreateUser && (
                <form onSubmit={handleCreateUser} className="mt-3 space-y-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  {/* User fields */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                      <select
                        value={createUserForm.role}
                        onChange={(e) => setCreateUserForm(p => ({ ...p, role: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-purple-400"
                      >
                        <option value="branch_manager">Branch Manager</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <Input label="Full Name" value={createUserForm.name} onChange={(e) => setCreateUserForm(p => ({ ...p, name: e.target.value }))} required />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input label="Email" type="email" value={createUserForm.email} onChange={(e) => setCreateUserForm(p => ({ ...p, email: e.target.value }))} required />
                    <Input label="Password" type="password" value={createUserForm.password} onChange={(e) => setCreateUserForm(p => ({ ...p, password: e.target.value }))} required />
                  </div>

                  {/* Create Branch toggle */}
                  <label className="flex items-center gap-2 cursor-pointer select-none text-xs border-t border-gray-200 pt-2">
                    <input
                      type="checkbox"
                      checked={createUserWithBranch}
                      onChange={(e) => {
                        setCreateUserWithBranch(e.target.checked);
                        if (e.target.checked) setCreateUserBranch(p => ({ ...p, email: createUserForm.email }));
                      }}
                      className="w-3.5 h-3.5 accent-purple-600 rounded"
                    />
                    <span className="font-medium text-gray-700">Also create a Branch</span>
                  </label>

                  {createUserWithBranch && (
                    <div className="grid grid-cols-3 gap-2">
                      <Input label="Branch Name" value={createUserBranch.name} onChange={(e) => setCreateUserBranch(p => ({ ...p, name: e.target.value }))} required />
                      <Input label="Branch Email" type="email" value={createUserBranch.email} onChange={(e) => setCreateUserBranch(p => ({ ...p, email: e.target.value }))} />
                      <Input label="Branch Phone" value={createUserBranch.phone} onChange={(e) => setCreateUserBranch(p => ({ ...p, phone: e.target.value }))} />
                    </div>
                  )}

                  <Button type="submit" disabled={createUserLoading} className="w-full">
                    {createUserLoading ? 'Creating…' : `Create ${createUserWithBranch ? 'User + Branch' : 'User'} & Send Welcome Email`}
                  </Button>
                </form>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t border-gray-100">
            <Button type="submit" className="flex-1">{editMode ? 'Update' : 'Create'} Merchant</Button>
            <Button type="button" variant="secondary" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1">Cancel</Button>
          </div>
        </form>
      </Modal>
      {/* Branches Modal */}
      <Modal
        isOpen={showBranchesModal}
        onClose={() => { setShowBranchesModal(false); setBranches([]); setBranchesCustomer(null); }}
        title={`Branches — ${branchesCustomer?.businessName || ''}`}
        size="lg"
      >
        {branchesLoading ? (
          <div className="flex items-center justify-center h-32"><div className="spinner" /></div>
        ) : branches.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <FiGitBranch size={32} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">No branches found for this merchant</p>
          </div>
        ) : (
          <div className="space-y-3">
            {branches.map((branch) => (
              <div key={branch._id} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 p-2 bg-blue-100 text-blue-600 rounded-lg">
                    <FiGitBranch size={14} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      {branch.branchName} <span className="text-xs font-normal text-gray-400">({branch.branchCode})</span>
                    </p>
                    {branch.branchEmail && <p className="text-xs text-gray-500">{branch.branchEmail}</p>}
                    {branch.managerId ? (
                      <div className="flex items-center gap-1 mt-1">
                        <FiUser size={11} className="text-purple-500" />
                        <span className="text-xs text-purple-700 font-medium">{branch.managerId.fullName}</span>
                        <span className="text-xs text-gray-400">·</span>
                        <span className="text-xs text-gray-500">{branch.managerId.email}</span>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 mt-1">No manager assigned</p>
                    )}
                  </div>
                </div>
                {branch.managerId && (
                  <button
                    onClick={() => handleResetPassword(branch)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
                    title="Reset manager password"
                  >
                    <FiKey size={12} /> Reset Password
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Reset Manager Password Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => { setShowPasswordModal(false); setNewPassword(''); }}
        title="Reset Manager Password"
        size="sm"
      >
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Branch: <span className="font-semibold text-gray-800">{passwordBranch?.branchName}</span>
          </p>
          {passwordBranch?.managerId && (
            <p className="text-sm text-gray-600 mt-1">
              Manager: <span className="font-semibold text-gray-800">{passwordBranch.managerId.fullName}</span>
            </p>
          )}
        </div>
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <Input
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Min. 6 characters"
            required
          />
          <div className="flex gap-2 pt-1">
            <Button type="submit" className="flex-1" disabled={passwordLoading}>
              {passwordLoading ? 'Updating…' : 'Update Password'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => { setShowPasswordModal(false); setNewPassword(''); }} className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Customers;
